import { useState, useEffect } from "react";
import { check_today_partial_order } from "src/DAL/shop";

export const useCartHook = () => {
  const [todayPartialOrderStatus, setTodayPartialOrderStatus] = useState(false);
  const [cart, setCart] = useState([]);
  const [quantities, setQuantities] = useState({});

  // Initialize cart and quantities from local storage
  useEffect(() => {
    const savedCart = localStorage.getItem("shop_cart");
    const savedQuantities = localStorage.getItem("shop_quantities");

    if (savedCart) {
      setCart(JSON.parse(savedCart));
      handleUpdateCartValues();
    }
    if (savedQuantities) {
      setQuantities(JSON.parse(savedQuantities));
    }
  }, []);

  // Save cart and quantities to local storage
  const saveToLocalStorage = (updatedCart, updatedQuantities) => {
    localStorage.setItem("shop_cart", JSON.stringify(updatedCart));
    localStorage.setItem("shop_quantities", JSON.stringify(updatedQuantities));
  };

  const handleQuantityChange = (item, value) => {
    const numValue =
      item.stock_type == "unlimited"
        ? value
        : Math.max(1, Math.min(item.quantity_remaining, parseInt(value) || 1));
    const updatedQuantities = {
      ...quantities,
      [item._id]: numValue,
    };
    setQuantities(updatedQuantities);
  };

  const handleAddToCart = (item) => {
    const quantity = quantities[item._id] || 1;
    const existingItem = cart.find((cartItem) => cartItem._id === item._id);

    let updatedCart;
    if (existingItem) {
      updatedCart = cart.map((cartItem) =>
        cartItem._id === item._id
          ? {
              ...cartItem,
              quantity:
                item.unit_type == "kilo_gram"
                  ? 1
                  : item?.stock_type == "unlimited"
                    ? cartItem.quantity + quantity
                    : Math.min(
                        item.quantity_remaining,
                        cartItem.quantity + quantity,
                      ),
              selectedQuantity: item.selectedQuantity,
            }
          : cartItem,
      );
    } else {
      updatedCart = [...cart, { ...item, quantity }];
    }

    setCart(() => [...updatedCart]);
    setQuantities({ ...quantities, [item._id]: 1 });
    saveToLocalStorage(updatedCart, { ...quantities, [item._id]: 1 });
    handleUpdateCartValues();

    return updatedCart;
  };

  const handleBuyNow = (item) => {
    const quantity = quantities[item._id] || 1;
    const existingItem = cart.find((cartItem) => cartItem._id === item._id);

    let updatedCart;
    if (existingItem) {
      updatedCart = cart.map((cartItem) =>
        cartItem._id === item._id
          ? {
              ...cartItem,
              quantity:
                item.unit_type == "kilo_gram"
                  ? 1
                  : item?.stock_type == "unlimited"
                    ? cartItem.quantity + quantity
                    : Math.min(
                        item.quantity_remaining,
                        cartItem.quantity + quantity,
                      ),
              selectedQuantity: item.selectedQuantity,
            }
          : cartItem,
      );
    } else {
      updatedCart = [...cart, { ...item, quantity }];
    }

    setCart(() => [...updatedCart]);
    setQuantities({ ...quantities, [item._id]: 1 });
    saveToLocalStorage(updatedCart, { ...quantities, [item._id]: 1 });
    handleUpdateCartValues();

    return updatedCart;
  };

  const handleRemoveFromCart = (itemId) => {
    const updatedCart = cart.filter((item) => item._id !== itemId);
    setCart(() => [...updatedCart]);
    saveToLocalStorage(updatedCart, quantities);
    handleUpdateCartValues();
  };

  const handleUpdateCartQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(itemId);
      return;
    }

    const updatedCart = cart.map((item) =>
      item._id === itemId
        ? {
            ...item,
            quantity:
              item.stock_type == "unlimited"
                ? newQuantity
                : Math.min(item.quantity_remaining, newQuantity),
          }
        : item,
    );
    setCart(() => [...updatedCart]);
    saveToLocalStorage(updatedCart, quantities);
    handleUpdateCartValues();
  };

  const handleOrderDone = () => {
    setCart((prev) => []);
    setQuantities({});
    localStorage.removeItem("shop_cart");
    localStorage.removeItem("shop_quantities");
  };

  const getCartValue = () => {
    return cart;
  };

  const handleGetPartialOrderStatus = async () => {
    const resp = await check_today_partial_order();
    setTodayPartialOrderStatus(resp?.data?.is_today_order || false);
    return resp?.data?.is_today_order || false;
  };

  const handleUpdateCartValues = () => {
    let partialApplied = false;
    setCart((prev) =>
      prev.map((obj) => {
        const shouldApplyPartial =
          !partialApplied &&
          !todayPartialOrderStatus &&
          obj.sponsorship_type === "partial";

        if (shouldApplyPartial) {
          partialApplied = true;
        }

        return {
          ...obj,
          total_amount: shouldApplyPartial
            ? (obj.quantity - 1) * obj.unit_cost
            : obj.unit_type == "kilo_gram"
              ? Math.round(obj.selectedQuantity * obj.unit_cost)
              : obj.quantity * obj.unit_cost,
          actual_total_amount:
            obj.unit_type == "kilo_gram"
              ? Math.round(obj.selectedQuantity * obj.unit_cost)
              : obj.quantity * obj.unit_cost,
          is_chargeable: !(shouldApplyPartial && obj.quantity == 1),
        };
      }),
    );
  };

  useEffect(() => {
    handleUpdateCartValues();
  }, [todayPartialOrderStatus]);

  return {
    cart,
    setCart,
    handleUpdateCartValues,
    todayPartialOrderStatus,
    handleGetPartialOrderStatus,
    quantities,
    getCartValue,
    handleQuantityChange,
    handleAddToCart,
    handleBuyNow,
    handleRemoveFromCart,
    handleUpdateCartQuantity,
    handleOrderDone,
  };
};
